import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getSebHealthService } from '../../../services/portalServices';
import type { SebHealth } from '../../../client/portal/types.gen';
import { partnersConfig } from '../../../utils/partners';
import formatDateTime from '../../../utils/formatDateTime';

type Props = {
  /** Bump to fetch again, e.g. after a sync. The status is otherwise fetched once per view. */
  refreshToken: number;
};

const STALE_RUN_MS = 5 * 60 * 1000;

// The backend's Maybe fields are optional in the spec (so `undefined` in the
// generated types) but arrive as `null` on the wire, hence `== null` below.
const formatOrNever = (date: string | null | undefined) =>
  date == null ? 'never' : formatDateTime(date);

const partnerDisplayName = (partnerName: string) =>
  partnersConfig[partnerName]?.name || partnerName;

const isRunStale = (lastSuccessfulRunAt: string | null | undefined) =>
  lastSuccessfulRunAt == null ||
  Date.now() - new Date(lastSuccessfulRunAt).getTime() > STALE_RUN_MS;

const SebHealthBanner = ({ refreshToken }: Props) => {
  const [health, setHealth] = useState<SebHealth | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      setHealth(await getSebHealthService());
      setFetchError(null);
    } catch (error) {
      setFetchError((error as Error).message);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth, refreshToken]);

  if (fetchError !== null) {
    return (
      <Banner status='problem'>
        <strong>SEB integration status unavailable.</strong> {fetchError}
      </Banner>
    );
  }

  if (health === null) {
    return <Banner status='loading'>Checking SEB integration status…</Banner>;
  }

  const runStale = isRunStale(health.lastSuccessfulRunAt);
  const hasProblem =
    runStale || health.unimportedFiles.length > 0 || health.unsent.length > 0;

  const ignoredUnsent =
    health.ignoredUnsent > 0 ? (
      <Muted>
        {health.ignoredUnsent} unsent{' '}
        {health.ignoredUnsent === 1 ? 'transaction' : 'transactions'} on ignored
        accounts.
      </Muted>
    ) : null;

  if (!hasProblem) {
    return (
      <Banner status='ok'>
        SEB integration OK, last statement{' '}
        {formatOrNever(health.lastCamt53ImportAt)}, last status report{' '}
        {formatOrNever(health.lastPain002ImportAt)}, nothing waiting.
        {ignoredUnsent}
      </Banner>
    );
  }

  return (
    <Banner status='problem'>
      <strong>SEB integration needs attention.</strong> Last statement{' '}
      {formatOrNever(health.lastCamt53ImportAt)}, last status report{' '}
      {formatOrNever(health.lastPain002ImportAt)}.
      {runStale && (
        <Section>
          <strong>
            The import loop has not completed a run since{' '}
            {formatOrNever(health.lastSuccessfulRunAt)}.
          </strong>
          {health.lastRunError != null && (
            <ErrorText>Last error: {health.lastRunError}</ErrorText>
          )}
        </Section>
      )}
      {health.unimportedFiles.length > 0 && (
        <Section>
          <strong>
            Files from SEB still in the bucket ({health.unimportedFiles.length}):
          </strong>
          <ul>
            {health.unimportedFiles.map(file => (
              <li key={file.objectKey}>
                <code>{file.objectKey}</code>
                {file.importError == null ? (
                  <> (waiting for the first import attempt)</>
                ) : (
                  <ErrorText>{file.importError}</ErrorText>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {health.unsent.length > 0 && (
        <Section>
          <strong>Transactions not yet sent:</strong>
          <ul>
            {health.unsent.map(entry => (
              <li key={entry.account}>
                <strong>
                  {entry.partner == null
                    ? 'No partner uses this account'
                    : partnerDisplayName(entry.partner)}
                </strong>{' '}
                (<code>{entry.account}</code>): {entry.count}{' '}
                {entry.count === 1 ? 'transaction' : 'transactions'}, oldest
                booking day {entry.oldestBookingDay}
                {entry.lastError != null && (
                  <ErrorText>Last error: {entry.lastError}</ErrorText>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {ignoredUnsent}
    </Banner>
  );
};

export default SebHealthBanner;

type BannerStatus = 'ok' | 'problem' | 'loading';

const bannerColors: {
  [status in BannerStatus]: { background: string; border: string; text: string };
} = {
  ok: { background: '#f1faf3', border: '#3f9d5a', text: '#1f5a30' },
  problem: { background: '#fff4f4', border: '#d0342c', text: '#7a1f1a' },
  loading: { background: '#fafafa', border: '#ebebeb', text: '#6b6b6b' },
};

const Banner = styled.div<{ status: BannerStatus }>`
  background: ${({ status }) => bannerColors[status].background};
  border: 1px solid ${({ status }) => bannerColors[status].border};
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: ${({ status }) => bannerColors[status].text};

  ul {
    margin: 0.25rem 0 0;
    padding-left: 1.25rem;
  }

  code {
    font-size: 0.85rem;
  }
`;

const Section = styled.div`
  margin-top: 0.5rem;
`;

const ErrorText = styled.div`
  font-family: monospace;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Muted = styled.div`
  margin-top: 0.5rem;
  opacity: 0.75;
`;
